<!--
    GesTip.vue
    ═══════════════════════════════════════════════════════════════════════════════

    Component de rendu global du tooltip. À monter UNE SEULE FOIS à la racine de 
    l'application. Il se téléporte dans <body> pour ne jamais être rogné par un
    parent avec overflow:hidden.

    Configuration — à ajouter dans App.vue (ou layout racine). Il n'est pas essentiel
    d'importer le component dans le App.vue puisqu'il est déjà importé dans main.js
        <template>
            ...
            <router-view />
            ...
            <GesTip />
        </template>

    Enregistrement de la directive et du component dans main.js :
        import tooltipDirective from './custom/gestip/tooltipDirective';
        import GesTip from './custom/gestip/GesTip.vue';

        ** Après le createApp
        ...
        app.directive('ges-tip', tooltipDirective);
        app.component('GesTip', GesTip);
        ...
        ** Avant app.mount('#app');
        
    ─── FONCTIONNEMENT INTERNE ───────────────────────────────────────────────────

    1. tooltipState.visible passe à true → le div tooltip est rendu dans <body>
    2. `measuring` est true pendant quelques frames pendant que tooltipService.js 
        lit les dimensions de l'élément. La classe `.measuring` applique visibility:hidden
        pour éviter un flash visuel.
    3. tooltipService.js écrit les valeurs finales x/y/placement/arrowX/arrowY dans l'état.
    4. `measuring` passe à false → le tooltip apparaît à sa position correcte.

    ─── MODES DE CONTENU ─────────────────────────────────────────────────────────

    Texte brut (défaut, html: false)
        Le contenu est rendu comme du texte. Protégé contre le XSS.

    Mode HTML (html: true)
        Le contenu est rendu avec v-html. À utiliser uniquement si le contenu est
        généré côté serveur ou est entièrement sous votre contrôle

    ─── CLASSES PERSONNALISÉES ───────────────────────────────────────────────────

    Des classes supplémentaires peuvent être passées via la directive.

    'tooltip-warning' est prédéfini dans GesTip.scss à titre d'exemple (texte rouge). 
    Ajoutez vos propres variantes dans GesTip.scss selon vos besoins.

    ─── INTERACTION AU SURVOL ────────────────────────────────────────────────────

    Le tooltip a pointer-events: none pour ne pas interférer avec les clics.
    Les handlers mouseenter/mouseleave appellent setTooltipHover() pour que déplacer
    le curseur du déclencheur VERS le tooltip ne déclenche pas de fermeture.

-->
<style>
    /* Visuellement masqué pendant que le service mesure les dimensions rendues du tooltip. */
    .ges-tooltip.measuring { visibility: hidden; }
</style>

<template>
    <!-- Teleport rend le tooltip comme enfant direct de <body>,
        évitant ainsi d'être rogné par le overflow:hidden d'un ancêtre -->
    <Teleport to="body">
        <div 
            v-if="tooltip.visible" 
            class="ges-tooltip" 
            ref="tooltipEl"
            :class="tooltipClasses" 
            :style="tooltipStyle"
            @mouseenter="onEnter"
            @mouseLeave="onLeave"
        >
            <!-- Contenu : mode HTML -->
            <div v-if="tooltip.html" v-html="content"></div>
            <!-- Contenu : par défaut -->
            <div v-else>{{ content }}</div>

            <!-- Flèche : la class de placement contrôle sur quel bord elle est placée
                 arrowStyle la positionne le long de ce bord via un offset en pixels. -->
            <div 
                class="tooltip-arrow" 
                :class="`arrow-${tooltip.placement}`" 
                :style="arrowStyle"
            ></div>
        </div>
    </Teleport>
</template>

<script>
    import { tooltipState, tooltipContent, setTooltipHover, hideTooltip } from '@/custom/gestip/tooltipService';
    export default {
        name: "GesTip",
        computed: {
            tooltip() {
                return tooltipState; // Expose l'objet d'état réactif complet au template
            },
            content() {
                return tooltipContent.value; // Contenu du tooltip résolu (texte brut ou HTML)
            },
            tooltipClasses() {
                return [
                    { measuring: tooltipState.measuring },
                    ...tooltipState.classes
                ]; // Fusionne la classe de visibilité `measuring` avec les classes fournies par l'option `classes`
            },
            /**
             * Positionne la boîte du tooltip via des coordonnées fixed calculées par tooltipService.js.
             * maxWidth est également appliqué ici pour que le service puisse tenir compte du retour
             * à la ligne lors de la mesure de la hauteur rendue du tooltip.
             */
            tooltipStyle() {
                return {
                    left: `${tooltipState.x}px`,
                    top: `${tooltipState.y}px`,
                    maxWidth: `${tooltipState.maxWidth}px`,
                };
            },
            /**
             * Positionne la flèche sur l'axe perpendiculaire à la direction du placement.
             *   Placements top/bottom → la flèche se déplace horizontalement   (left: arrowX px)
             *   Placements left/right → la flèche se déplace verticalement     (top: arrowY px)
             */
            arrowStyle() {
                const placement = tooltipState.placement;

                if (placement === 'top' || placement === 'bottom') {
                    return { left: `${tooltipState.arrowX}px` };
                }

                return { top: `${tooltipState.arrowY}px` };
            }
        },
        method: {
            // Indique au service que le curseur est dans le tooltip pour éviter la fermeture automatique
            onEnter() {
                setTooltipHover(true);
            },
            // Le curseur a quitté la boîte du tooltip — démarre le timer de fermeture
            onLeave() {
                setTooltipHover(false);
                hideTooltip();
            }
        }
    }
</script>